import { keyframes } from '@emotion/react'

import { Box, Tooltip } from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { SIDE_MENU_DOT_ATTR } from '~/components/layouts/MainLayout/sideMenuDotPulse'
import {
  type SideMenuDot as SideMenuDotModel,
  sideMenuDotLabel,
} from '~/models/sideMenuDot'

const DOT_SIZE = '16px'
const DOT_ICON_SIZE = '10px'

// design `.nav-menu-alert-icon` / `--warn`: 赤 = `--surface-negative-high`、
// 黄 = `--border-warning-mid` (`Object.warningMid` は白背景のサイドバーに沈む)。
// 色は `color` に token を置き、地色と光は `currentColor` から導く (raw 値を書かない)。
const DOT_COLOR = {
  negative: 'Surface.negativeHigh',
  warning: 'Border.warningMid',
} as const

// design `navMenuAlertGlow` / `navMenuAlertGlowWarn`: 脈打つ光 (1.4 秒)。
const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 70%, transparent); }
  50% { box-shadow: 0 0 0 6px color-mix(in srgb, currentColor 0%, transparent); }
`

type Props = {
  dot: SideMenuDotModel
  /**
   * 行の背景を明滅させている行 (項目) では、ドット自身の光は止めて
   * サイドメニューの地色の縁取りにする (design #9306 ③)。
   */
  rowPulses: boolean
}

/**
 * サイドメニューのドット (AC-SC-004 / 005 / 034 / 035 / 041 / 042 共通の描画)。
 * 色だけに意味を持たせない: 理由を tooltip と aria-label の両方に載せる。
 * クリック導線を持たない (行のクリックはそのまま親の要素が受ける)。
 *
 * [DS差異] DS に「文字の無い小さな丸」の component が無いため独自の部品。
 * 色・余白は token 文字列で指定している (design の見た目に合わせた 16px の丸)。
 */
export const SideMenuDot = ({ dot, rowPulses }: Props): React.ReactElement => {
  const label = sideMenuDotLabel(dot)
  return (
    <Tooltip content={label}>
      <Box
        as="span"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        display="inline-flex"
        width={DOT_SIZE}
        height={DOT_SIZE}
        color={DOT_COLOR[dot.severity]}
        borderRadius="full"
        aria-label={label}
        backgroundColor="currentColor"
        css={
          rowPulses
            ? { boxShadow: '0 0 0 2px {colors.Surface.secondary}' }
            : { animation: `${glow} 1.4s ease-in-out infinite` }
        }
        role="img"
        {...{ [SIDE_MENU_DOT_ATTR]: dot.severity }}
      >
        <FaTriangleExclamationSolid
          color="Object.highEmphasisInverse"
          aria-hidden
          style={{ width: DOT_ICON_SIZE, height: DOT_ICON_SIZE }}
        />
      </Box>
    </Tooltip>
  )
}
